import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import * as React from 'react';
import { useTitleStore } from 'src/stores/useTitleStore';
import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import { createColumnHelper } from '@tanstack/react-table';
import { commonMessages } from 'src/utils/messages';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { servicePlanCategoriesMessages } from 'src/features/servicePlanCategory/utils/messages';
import { useListServicePlanCategories } from 'src/features/servicePlanCategory/api/useListServicePlanCategories';
import { ServicePlanCategory } from 'src/features/servicePlanCategory/utils/types';
import CreateServicePlanCategoryFormModal from 'src/features/servicePlanCategory/components/forms/CreateServicePlanCategoryFormModal';
import CellServicePlanCategoryActions from 'src/features/servicePlanCategory/components/tables/CellServicePlanCategoryActions';
import usePagination from 'src/registry/hooks/usePagination';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const ServicePlanCategoriesTable = () => {
    const { formatMessage } = useIntl();

    const permissionManageServicePlan = useHasPermissions([PERMISSION.MANAGE_SERVICE_PLANS]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(servicePlanCategoriesMessages.servicePlanCategories));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const {
        data: servicePlanCategories,
        isLoading,
        isError,
    } = useListServicePlanCategories({
        ...querySearchOptions,
        ...queryPaginationOptions,
    });

    const columnHelper = createColumnHelper<ServicePlanCategory>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('code', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(servicePlansMessages.code),
            },
        }),
        ...(permissionManageServicePlan
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellServicePlanCategoryActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<ServicePlanCategory, unknown>
            columns={columns}
            data={servicePlanCategories}
            semantics={{
                title: formatMessage(servicePlanCategoriesMessages.servicePlanCategories),
                entryName: formatMessage(servicePlanCategoriesMessages.category).toLowerCase(),
                entryNames: formatMessage(servicePlanCategoriesMessages.categories).toLowerCase(),
                placeholder: formatMessage(servicePlanCategoriesMessages.category).toLowerCase(),
            }}
            search={search}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageServicePlan && { newEntry: CreateServicePlanCategoryFormModal }),
            }}
        />
    );
};

export default ServicePlanCategoriesTable;
